import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {Subscription, timer} from 'rxjs';
import {
  KybDisplayStatus,
  KybRefreshFn,
  KybResult,
  KybResultFn,
  kybDisplayStatus,
} from '@portal-workspace/grow-shared-library';

/**
 * The waiting state of a FrankieOne KYB check.
 *
 * Two modes. Given a `pollFn` it owns the wait: it fetches immediately, polls until its entity
 * resolves, and emits the result to the host. Without one it is purely presentational and renders
 * whatever `status` it is handed.
 *
 * Which entity it waits on is `commercialEntityId`; omitting it waits on the applicant entity. Both
 * the company details step and each corporate partner/trustee "Select signers" panel drive it this
 * way, so there is one waiting implementation rather than one per screen.
 */
@Component({
  selector: 'kyb-status',
  templateUrl: './kyb-status.component.html',
  styleUrls: ['./kyb-status.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
})
export class KybStatusComponent implements OnInit, OnChanges, OnDestroy {

  /** Presentational mode only; ignored once `pollFn` is supplied. */
  @Input() status: KybDisplayStatus = 'pending';
  @Input() applicationId: number | null = null;
  /**
   * Which entity of the application to wait on. Null waits on the applicant entity, which is what
   * the company details step needs; a corporate partner/trustee panel passes its own id so it reads
   * that company's run rather than the applicant's.
   */
  @Input() commercialEntityId: number | null = null;
  /** Named while waiting, so it is obvious which entity is being looked up. */
  @Input() businessName: string | null = null;
  /**
   * Whether the panel is on screen. The stepper keeps every step's content alive, so without this
   * the wait would begin when the page loads: a broker who spends a minute on the previous step
   * would arrive to find the check already described as slow, and would have been polled for the
   * whole time they were nowhere near this step. Only gates the start — once the wait is under way
   * it keeps running, so stepping away and back does not restart it.
   */
  @Input() active = true;
  /** Supplying this switches the component into polling mode. */
  @Input() pollFn: KybResultFn | null = null;
  /**
   * Backs Retry. Unlike `pollFn` this is expected to have side effects: it pulls the result from
   * FrankieOne directly rather than reading back what has already been stored. It never starts a new
   * run, because that costs a billable workflow execution. Falls back to `pollFn`.
   */
  @Input() refreshFn: KybRefreshFn | null = null;

  /** Presentational mode only: the host owns the retry. */
  @Output() retry = new EventEmitter<void>();
  /** Polling mode: this panel's entity check finished, carrying the whole refreshed read model. */
  @Output() resolved = new EventEmitter<KybResult>();
  /** Offered after 2 minutes or immediately on terminal failure; polling continues in the background. */
  @Output() manualEntry = new EventEmitter<void>();

  // A check normally lands well inside this, so it is the point at which the user is offered a way
  // to intervene rather than a deadline: polling continues, just less often, because a run that
  // completes after the threshold should still resolve on its own.
  private static readonly RETRY_THRESHOLD_MS = 2 * 60 * 1000;
  private static readonly POLL_INTERVAL_MS = 5 * 1000;
  private static readonly SLOW_POLL_INTERVAL_MS = 15 * 1000;
  // A pull usually answers in a fraction of a second, so surfacing that answer the instant it lands
  // flickers the whole panel through three states on one click. Holding the checking state long
  // enough to read is what stops that, and it doubles as the throttle on the button.
  private static readonly RETRY_HOLD_MS = 5 * 1000;

  displayStatus: KybDisplayStatus = 'pending';
  firstReadDone = false;
  /** Whether Retry is offered: past the threshold, or straight away once a run has failed. */
  showRetry = false;
  /**
   * Whether the wait itself has passed the threshold. Deliberately not the same thing as
   * `showRetry`: a run that fails and is then restarted by the background queue offers Retry while
   * the new run is only seconds old, and the copy must still describe a wait that has just begun.
   */
  waitingLong = false;
  retrying = false;

  private readonly cdr = inject(ChangeDetectorRef);
  // Measured from when the panel appeared rather than from the first fetch, so that a late-arriving
  // `applicationId` cannot leave this at 0 and make every elapsed check read as decades.
  private startedAt = Date.now();
  private started = false;
  private pollSubscription: Subscription | null = null;
  private fetchSubscription: Subscription | null = null;
  private retryHoldSubscription: Subscription | null = null;

  /**
   * The host has handed the wait over. Distinct from `polling` because the application id can
   * arrive a beat after the panel does: this is already true in that gap, which is what keeps the
   * presentational Retry button from flashing up before the first read has even been made.
   */
  get owned(): boolean {
    return !!this.pollFn;
  }

  get polling(): boolean {
    return this.owned && this.applicationId != null;
  }

  /**
   * The panel has two looks, and a retry in flight always wears the waiting one. Keeping the
   * failure styling up during a retry made the click look like it had done nothing.
   */
  get showingFailure(): boolean {
    return !this.retrying && this.displayStatus === 'failed';
  }

  /**
   * Nothing is rendered until the first read has answered. The check has almost always completed
   * long before the broker reaches this step, and drawing the wait during that one round trip
   * flashed "Looking up …" onto a step that was about to show the form.
   */
  get showPanel(): boolean {
    return !this.owned || this.firstReadDone;
  }

  get showAction(): boolean {
    return !this.owned || this.showRetry || this.displayStatus === 'failed';
  }

  get actionDisabled(): boolean {
    return this.retrying;
  }

  get actionLabel(): string {
    return this.retrying ? 'Checking...' : 'Retry';
  }

  get waitTitle(): string {
    if (this.showingFailure) {
      return `We couldn't get the company details`;
    }
    if (this.retrying) {
      return 'Checking now';
    }
    const name = this.businessName?.trim();
    return name ? `Looking up ${name}` : 'Looking up this business';
  }

  get waitMessage(): string {
    // Both failure statuses are only reached once the background queue has exhausted its attempts,
    // and Retry only pulls an existing execution, so there is nothing left here for the user to do.
    if (this.showingFailure) {
      return `Your progress is saved. Please contact support to have this check run again.`;
    }
    if (this.retrying) {
      return 'One moment.';
    }
    return this.waitingLong
      ? `We'll keep checking and fill in the details automatically. Your progress is saved, so you can come back to this application later.`
      : 'This usually takes a few moments.';
  }

  ngOnInit(): void {
    this.syncInputs();
  }

  ngOnChanges(): void {
    this.syncInputs();
  }

  /**
   * Polling starts from whichever hook first sees the panel on screen with an application id,
   * rather than from `ngOnInit` alone: the step is constructed with the page and the id it needs
   * only exists once the draft has been saved, so neither is reliably true at init.
   */
  private syncInputs(): void {
    if (this.started) {
      return;
    }
    if (this.active && this.polling) {
      this.started = true;
      this.startedAt = Date.now();
      // Immediate first read, so re-entering the step with the check already done never waits.
      this.fetch(this.pollFn as KybResultFn);
      return;
    }
    if (!this.owned) {
      this.displayStatus = this.status;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.fetchSubscription?.unsubscribe();
    this.retryHoldSubscription?.unsubscribe();
  }

  onRetry(): void {
    if (this.retrying) {
      return;
    }
    this.retrying = true;
    this.holdRetryingState();
    if (!this.polling) {
      this.retry.emit();
      this.cdr.markForCheck();
      return;
    }
    this.stopPolling();
    this.cdr.markForCheck();
    this.fetch(this.refreshFn ?? (this.pollFn as KybResultFn));
  }

  onManualEntry(): void {
    this.manualEntry.emit();
  }

  // Typed as the wider seam so both pollFn and refreshFn go through here; pollFn simply ignores the
  // second argument.
  private fetch(fn: KybRefreshFn): void {
    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = fn(this.applicationId as number, this.commercialEntityId).subscribe({
      next: result => this.onResult(result),
      // A failed request is indistinguishable to the user from a slow one, and the next poll may
      // well succeed, so it keeps waiting rather than reporting the check itself as failed.
      error: () => this.afterAttempt(),
    });
  }

  private onResult(result: KybResult): void {
    const status = entityDisplayStatus(result, this.commercialEntityId);
    if (status === 'resolved') {
      // Handing over to the form is the outcome the user pressed for, so it is never held back.
      this.stopPolling();
      this.releaseRetryingState();
      this.displayStatus = 'resolved';
      this.cdr.markForCheck();
      this.resolved.emit(result);
      return;
    }
    this.displayStatus = status;
    // A failed run needs the user to act, so Retry is offered without waiting out the threshold.
    if (status === 'failed') {
      this.showRetry = true;
    }
    this.afterAttempt();
  }

  private afterAttempt(): void {
    // Reached on a pending result, a failed one, and a failed request alike: all three mean there is
    // genuinely something to wait for, which is the point at which the panel is worth drawing.
    this.firstReadDone = true;
    // An answer that arrives inside the hold is kept back until it ends, so the panel changes once.
    if (!this.retryHoldSubscription) {
      this.retrying = false;
    }
    const elapsed = Date.now() - this.startedAt;
    if (elapsed >= KybStatusComponent.RETRY_THRESHOLD_MS) {
      this.waitingLong = true;
      this.showRetry = true;
    }
    this.cdr.markForCheck();
    this.schedulePoll(elapsed);
  }

  private holdRetryingState(): void {
    this.retryHoldSubscription?.unsubscribe();
    this.retryHoldSubscription = timer(KybStatusComponent.RETRY_HOLD_MS).subscribe(() => {
      this.retryHoldSubscription = null;
      this.retrying = false;
      this.cdr.markForCheck();
    });
  }

  private releaseRetryingState(): void {
    this.retryHoldSubscription?.unsubscribe();
    this.retryHoldSubscription = null;
    this.retrying = false;
  }

  private schedulePoll(elapsedMs: number): void {
    this.stopPolling();
    const interval = elapsedMs >= KybStatusComponent.RETRY_THRESHOLD_MS
      ? KybStatusComponent.SLOW_POLL_INTERVAL_MS
      : KybStatusComponent.POLL_INTERVAL_MS;
    this.pollSubscription = timer(interval).subscribe(() => this.fetch(this.pollFn as KybResultFn));
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }
}

/**
 * The status of the one entity this panel is waiting on, picked out of the whole read model.
 *
 * With no id it is the applicant entity, whose own check decides whether the company details step can
 * be shown; corporate trustees and partners are deliberately not waited on there, because their
 * checks are surfaced on the Key People step next to the rows that depend on them. Each of those rows
 * then names its own entity here, so a still-running partner never reads its parent's completed check
 * as its own.
 *
 * An entity missing from the result reads as pending: its run row is created before the poll that
 * first sees it, so absence means "not visible yet", not "nothing to wait for".
 */
const entityDisplayStatus = (
  result: KybResult | null,
  commercialEntityId: number | null,
): KybDisplayStatus => {
  const entity = (result?.entities ?? []).find(candidate => commercialEntityId == null
    ? candidate.isPrimary
    : candidate.commercialEntityId === commercialEntityId) ?? null;
  return entity?.mode === 'MANUAL'
    ? 'resolved'
    : entity
      ? kybDisplayStatus(entity.status)
      : 'pending';
};
